import type { RouteOrderDto } from '../../dto/transport';

export interface DeliveryStopOrder {
  orderId: string;
  orderNumber: string;
  customerName: string | null;
}

export interface DeliveryStop {
  /** null = orders whose saved unloading point matches none of the truck's addresses. */
  address: string | null;
  orders: DeliveryStopOrder[];
}

const normalize = (value: string | null | undefined) => (value || '').trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Unloading points of one truck, each with the customer orders unloaded there
 * (`RouteOrderDto.deliveryAddress`, saved per order by the transport form).
 * Presentation only: an order without a matching point goes to the only stop
 * when there is one, otherwise to a trailing `address: null` group.
 */
export function buildDeliveryStops(
  deliveryAddresses: string[],
  routeOrders: Pick<RouteOrderDto, 'orderId' | 'deliveryAddress' | 'order'>[],
): DeliveryStop[] {
  const stops: DeliveryStop[] = deliveryAddresses
    .filter((address) => address.trim().length > 0)
    .map((address) => ({ address, orders: [] }));
  const unassigned: DeliveryStopOrder[] = [];

  for (const routeOrder of routeOrders) {
    const entry: DeliveryStopOrder = {
      orderId: routeOrder.orderId,
      orderNumber: routeOrder.order?.displayOrderNumber || routeOrder.order?.orderNumber || '',
      customerName: routeOrder.order?.customerName || null,
    };
    const key = normalize(routeOrder.deliveryAddress);
    const stop = key ? stops.find((candidate) => normalize(candidate.address) === key) : undefined;
    if (stop) stop.orders.push(entry);
    else if (stops.length === 1) stops[0].orders.push(entry);
    else unassigned.push(entry);
  }

  return unassigned.length > 0 ? [...stops, { address: null, orders: unassigned }] : stops;
}

/** Stop number (1…n, route order; the „bez przypisanego punktu” group last) of each order. */
export function deliveryStopNumberByOrderId(stops: DeliveryStop[]): Map<string, number> {
  const numbers = new Map<string, number>();
  stops.forEach((stop, index) => stop.orders.forEach((order) => numbers.set(order.orderId, index + 1)));
  return numbers;
}
